import * as React from "react";
import { Chat, useChat } from "@ai-sdk/react";
import { DefaultChatTransport, lastAssistantMessageIsCompleteWithToolCalls, type UIMessage } from "ai";
import type { SupabaseClient } from "@supabase/supabase-js";
import { generateChatSuggestions } from "../../../services/geminiService";
import {
  RENDER_TOOL_PREFIX,
  buildToolDeclarations,
  lastStepToolNames,
  plainTextOf,
  toLegacyMessage,
} from "./messages";
import * as threadStore from "./threadStore";
import type {
  AssistantSuggestion,
  AssistantThreadSummary,
  CartAssistantComponent,
  CartAssistantTool,
  LegacyThreadMessage,
} from "./types";

/**
 * CartAssistant runtime on the Vercel AI SDK.
 *
 * - The model is reached through /api/ai/chat (Gemini key stays server-side).
 * - Tools and generative cards run in the browser:
 *   the server only sees their JSON Schemas. Cards are `render_<Name>` tools
 *   whose input is the card's props.
 * - Threads persist to public.assistant_threads (RLS by Clerk user).
 * - The system prompt and context helpers are sent with every request, so
 *   prompt changes apply to existing threads too.
 */

const MAX_AUTO_STEPS = 12;
// Gemini 2.5 Flash intermittently answers with STOP and zero output tokens.
const MAX_EMPTY_REPLY_RETRIES = 1;
const SAVE_DEBOUNCE_MS = 800;

const newThreadId = () => `thread_${crypto.randomUUID()}`;

/** Cheap change detector so opening a saved thread doesn't immediately re-save (and re-sort) it. */
const conversationSignature = (threadId: string, messages: UIMessage[]) => {
  const last = messages[messages.length - 1];
  return `${threadId}:${messages.length}:${last?.id ?? ""}:${last ? JSON.stringify(last.parts).length : 0}`;
};

const CHAT_RENDER_THROTTLE_MS = 50;

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  try {
    const token = await (window as any).Clerk?.session?.getToken({ template: "supabase" });
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch (err) {
    console.warn("[CartAssistant] Failed to get Clerk token:", err);
    return {};
  }
};

const evaluateContext = (helpers: Record<string, () => unknown>) => {
  const context: Record<string, unknown> = {};
  for (const [key, fn] of Object.entries(helpers)) {
    try {
      context[key] = fn();
    } catch (err) {
      context[key] = null;
    }
  }
  return context;
};

interface StagedImage {
  id: string;
  file: File;
}

interface CartAssistantContextValue {
  currentThreadId: string;
  messages: LegacyThreadMessage[];
  thread: { thread: { messages: LegacyThreadMessage[]; status: string } };
  streamingState: { status: "idle" | "waiting" | "streaming" };
  isStreaming: boolean;
  isWaiting: boolean;
  isIdle: boolean;
  error: Error | undefined;
  sendMessage: (text: string) => Promise<void>;
  cancelRun: () => Promise<void>;
  startNewThread: () => void;
  switchThread: (id: string) => Promise<void>;

  input: {
    value: string;
    setValue: (value: string) => void;
    submit: () => Promise<void>;
    isPending: boolean;
    isDisabled: boolean;
    images: StagedImage[];
    addImage: (file: File) => Promise<void>;
    removeImage: (id: string) => void;
    clearImages: () => void;
  };

  threads: AssistantThreadSummary[];
  threadsLoading: boolean;
  refetchThreads: () => Promise<void>;
  deleteThread: (id: string) => Promise<void>;
  renameThread: (id: string, name: string) => Promise<void>;
  loadThreadMessages: (id: string) => Promise<LegacyThreadMessage[]>;

  suggestions: AssistantSuggestion[];
  isGeneratingSuggestions: boolean;
}

const CartAssistantContext = React.createContext<CartAssistantContextValue | null>(null);

export interface CartAssistantProviderProps {
  tools: CartAssistantTool[];
  components: CartAssistantComponent[];
  systemPrompt: string;
  contextHelpers: Record<string, () => unknown>;
  userKey?: string;
  supabase: SupabaseClient | null;
  language: string;
  children: React.ReactNode;
}

export function CartAssistantProvider({
  tools,
  components,
  systemPrompt,
  contextHelpers,
  userKey,
  supabase,
  language,
  children,
}: CartAssistantProviderProps) {
  const toolDeclarations = React.useMemo(() => buildToolDeclarations(tools, components), [tools, components]);

  // Always-current values for callbacks owned by the long-lived Chat instance.
  const latest = React.useRef({ tools, toolDeclarations, systemPrompt, contextHelpers, supabase, userKey, language });
  latest.current = { tools, toolDeclarations, systemPrompt, contextHelpers, supabase, userKey, language };

  const [session, setSession] = React.useState<{ id: string; messages: UIMessage[]; name: string | null }>(() => ({
    id: newThreadId(),
    messages: [],
    name: null,
  }));
  const sessionRef = React.useRef(session);
  sessionRef.current = session;

  const [runningTools, setRunningTools] = React.useState(0);
  const autoStepsRef = React.useRef(0);
  const emptyRetriesRef = React.useRef(0);
  const createdAtRef = React.useRef(new Map<string, number>());
  const savedSignatureRef = React.useRef<string | null>(null);

  const chat = React.useMemo(() => {
    const instance: Chat<UIMessage> = new Chat<UIMessage>({
      id: session.id,
      messages: session.messages,
      transport: new DefaultChatTransport<UIMessage>({
        api: "/api/ai/chat",
        prepareSendMessagesRequest: async ({ messages, id }) => ({
          body: {
            id,
            messages,
            system: latest.current.systemPrompt,
            tools: latest.current.toolDeclarations,
            context: evaluateContext(latest.current.contextHelpers),
          },
          headers: await getAuthHeaders(),
        }),
      }),
      sendAutomaticallyWhen: ({ messages }) => {
        if (!lastAssistantMessageIsCompleteWithToolCalls({ messages })) return false;
        // A step that only rendered cards ends the turn — no need for another model round-trip.
        const stepTools = lastStepToolNames(messages[messages.length - 1]);
        if (!stepTools.some((name) => !name.startsWith(RENDER_TOOL_PREFIX))) return false;
        if (autoStepsRef.current >= MAX_AUTO_STEPS) {
          console.warn("[CartAssistant] Auto tool-step limit reached; waiting for the user.");
          return false;
        }
        autoStepsRef.current += 1;
        return true;
      },
      onToolCall: ({ toolCall }) => {
        const name = toolCall.toolName as string;
        const toolCallId = toolCall.toolCallId;

        if (name.startsWith(RENDER_TOOL_PREFIX)) {
          // No await inside onToolCall (AI SDK deadlock guidance).
          instance.addToolOutput({ tool: name, toolCallId, output: { displayed: true } });
          return;
        }

        const def = latest.current.tools.find((t) => t.name === name);
        if (!def) {
          instance.addToolOutput({ tool: name, toolCallId, state: "output-error", errorText: `Unknown tool: ${name}` });
          return;
        }

        const parsed = def.inputSchema.safeParse(toolCall.input ?? {});
        if (!parsed.success) {
          instance.addToolOutput({
            tool: name,
            toolCallId,
            state: "output-error",
            errorText: `Invalid input for ${name}: ${parsed.error.message}`,
          });
          return;
        }

        setRunningTools((n) => n + 1);
        Promise.resolve()
          .then(() => def.tool(parsed.data))
          .then(
            (output) => instance.addToolOutput({ tool: name, toolCallId, output: output ?? null }),
            (err) => {
              console.error(`[CartAssistant] Tool ${name} failed:`, err);
              instance.addToolOutput({
                tool: name,
                toolCallId,
                state: "output-error",
                errorText: err?.message || String(err),
              });
            },
          )
          .finally(() => setRunningTools((n) => Math.max(0, n - 1)));
      },
      onFinish: ({ message, isAbort, isDisconnect, isError }) => {
        if (isAbort || isDisconnect || isError || message.role !== 'assistant') return;
        const parts = message.parts as any[];
        const hasContent = (p: any) => (p.type === 'text' && p.text?.trim()) || p.type === 'dynamic-tool' || p.type?.startsWith('tool-');
        const lastStepStart = parts.map((p) => p.type).lastIndexOf('step-start');
        const lastStepEmpty = !parts.slice(lastStepStart + 1).some(hasContent);
        if (!lastStepEmpty || emptyRetriesRef.current >= MAX_EMPTY_REPLY_RETRIES) return;
        emptyRetriesRef.current += 1;
        console.warn('[CartAssistant] Empty model reply; retrying once.');
        // Nothing at all → regenerate the reply; empty only after tool results → continue it.
        setTimeout(() => {
          (parts.some(hasContent) ? instance.sendMessage() : instance.regenerate()).catch((err) =>
            console.error('[CartAssistant] Retry after empty reply failed:', err),
          );
        }, 0);
      },
      onError: (err) => console.error("[CartAssistant] Chat error:", err),
    });
    return instance;
    // A new Chat per thread; its seed messages are captured at creation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.id]);

  // The reply streams word by word (smoothStream in /api/ai/chat); updating the
  // thread at most every CHAT_RENDER_THROTTLE_MS keeps that smooth without
  // re-rendering the whole thread and its markdown for every word.
  const { messages, status, error } = useChat({ chat, throttle: CHAT_RENDER_THROTTLE_MS });

  const isStreaming = status === "streaming";
  const isWaiting = status === "submitted" || (runningTools > 0 && !isStreaming);
  const isIdle = !isStreaming && !isWaiting;
  const isIdleRef = React.useRef(isIdle);
  isIdleRef.current = isIdle;

  const legacyMessages = React.useMemo(() => {
    const now = Date.now();
    return messages.map((m) => {
      if (!createdAtRef.current.has(m.id)) {
        createdAtRef.current.set(m.id, (m.metadata as any)?.createdAt ?? now);
      }
      return toLegacyMessage(m, createdAtRef.current.get(m.id)!);
    });
  }, [messages]);

  // ---- Suggestions ----
  const [suggestions, setSuggestions] = React.useState<AssistantSuggestion[]>([]);
  const [isGeneratingSuggestions, setIsGeneratingSuggestions] = React.useState(false);
  const suggestedForRef = React.useRef<string | null>(null);

  // ---- Sending ----
  const sendMessage = React.useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      if (!isIdleRef.current) await chat.stop();
      autoStepsRef.current = 0;
      emptyRetriesRef.current = 0;
      setSuggestions([]);
      chat
        .sendMessage({ text: trimmed, metadata: { createdAt: Date.now() } })
        .catch((err) => console.error("[CartAssistant] Send failed:", err));
    },
    [chat],
  );

  const cancelRun = React.useCallback(async () => {
    await chat.stop();
  }, [chat]);

  // ---- Threads ----
  const [threads, setThreads] = React.useState<AssistantThreadSummary[]>([]);
  const [threadsLoading, setThreadsLoading] = React.useState(false);

  const refetchThreads = React.useCallback(async () => {
    const client = latest.current.supabase;
    if (!client || !latest.current.userKey) return;
    setThreadsLoading(true);
    try {
      const rows = await threadStore.listThreads(client);
      setThreads(rows.map((r) => ({ id: r.id, name: r.name, createdAt: r.created_at, updatedAt: r.updated_at })));
    } catch (err) {
      console.error("[CartAssistant] Failed to list threads:", err);
    } finally {
      setThreadsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (supabase && userKey) refetchThreads();
  }, [supabase, userKey, refetchThreads]);

  const startNewThread = React.useCallback(() => {
    chat.stop();
    autoStepsRef.current = 0;
    setSuggestions([]);
    suggestedForRef.current = null;
    setSession({ id: newThreadId(), messages: [], name: null });
  }, [chat]);

  const switchThread = React.useCallback(
    async (id: string) => {
      const client = latest.current.supabase;
      if (!client || id === sessionRef.current.id) return;
      try {
        const row = await threadStore.loadThread(client, id);
        if (!row) return;
        chat.stop();
        autoStepsRef.current = 0;
        setSuggestions([]);
        // Don't spend a model call re-suggesting for a conversation that's just being reopened.
        suggestedForRef.current = row.messages?.[row.messages.length - 1]?.id ?? null;
        savedSignatureRef.current = conversationSignature(row.id, row.messages || []);
        setSession({ id: row.id, messages: row.messages || [], name: row.name });
      } catch (err) {
        console.error("[CartAssistant] Failed to load thread:", err);
      }
    },
    [chat],
  );

  const deleteThread = React.useCallback(
    async (id: string) => {
      const client = latest.current.supabase;
      if (!client) return;
      await threadStore.deleteThread(client, id);
      if (id === sessionRef.current.id) startNewThread();
      await refetchThreads();
    },
    [refetchThreads, startNewThread],
  );

  const renameThread = React.useCallback(
    async (id: string, name: string) => {
      const client = latest.current.supabase;
      if (!client) return;
      await threadStore.renameThread(client, id, name);
      if (id === sessionRef.current.id) setSession((s) => ({ ...s, name }));
      await refetchThreads();
    },
    [refetchThreads],
  );

  const loadThreadMessages = React.useCallback(
    async (id: string): Promise<LegacyThreadMessage[]> => {
      if (id === sessionRef.current.id) return legacyMessages;
      const client = latest.current.supabase;
      if (!client) return [];
      const row = await threadStore.loadThread(client, id);
      return (row?.messages || []).map((m) => toLegacyMessage(m, Date.parse(row!.created_at)));
    },
    [legacyMessages],
  );

  // Persist the active thread once a turn settles.
  React.useEffect(() => {
    if (!isIdle || messages.length === 0 || !supabase || !userKey) return;
    const threadId = session.id;
    const signature = conversationSignature(threadId, messages);
    if (signature === savedSignatureRef.current) return;
    const timer = setTimeout(async () => {
      const name =
        sessionRef.current.name ||
        plainTextOf(messages.find((m) => m.role === "user")).slice(0, 60) ||
        null;
      const withTimestamps = messages.map((m) => ({
        ...m,
        metadata: { ...((m.metadata as object) || {}), createdAt: createdAtRef.current.get(m.id) ?? Date.now() },
      }));
      try {
        await threadStore.saveThread(supabase, { id: threadId, userId: userKey, name, messages: withTimestamps });
        savedSignatureRef.current = signature;
        if (!sessionRef.current.name && name && sessionRef.current.id === threadId) {
          setSession((s) => (s.id === threadId ? { ...s, name } : s));
        }
        refetchThreads();
      } catch (err) {
        console.error("[CartAssistant] Failed to save thread:", err);
      }
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [isIdle, messages, session.id, supabase, userKey, refetchThreads]);

  // Follow-up suggestion chips after each completed assistant turn.
  React.useEffect(() => {
    const last = messages[messages.length - 1];
    if (!isIdle || !last || last.role !== "assistant" || suggestedForRef.current === last.id) return;
    suggestedForRef.current = last.id;
    const transcript = messages
      .slice(-6)
      .map((m) => `${m.role === "user" ? "User" : "Assistant"}: ${plainTextOf(m)}`)
      .filter((line) => !line.endsWith(": "))
      .join("\n");
    let cancelled = false;
    setIsGeneratingSuggestions(true);
    generateChatSuggestions(transcript, latest.current.language)
      .then((items) => {
        if (cancelled) return;
        setSuggestions(items.map((text, i) => ({ id: `${last.id}-s${i}`, title: text, detailedSuggestion: text })));
      })
      .finally(() => {
        if (!cancelled) setIsGeneratingSuggestions(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isIdle, messages]);

  // ---- Shared input (the main textarea and the cards both drive it) ----
  const [inputValue, setInputValueState] = React.useState("");
  const inputValueRef = React.useRef("");
  const setInputValue = React.useCallback((value: string) => {
    inputValueRef.current = value;
    setInputValueState(value);
  }, []);
  const [images, setImages] = React.useState<StagedImage[]>([]);

  const submit = React.useCallback(async () => {
    const text = inputValueRef.current;
    setInputValue("");
    await sendMessage(text);
  }, [sendMessage, setInputValue]);

  const input = React.useMemo(
    () => ({
      value: inputValue,
      setValue: setInputValue,
      submit,
      isPending: false,
      isDisabled: !isIdle,
      images,
      addImage: async (file: File) => setImages((prev) => [...prev, { id: crypto.randomUUID(), file }]),
      removeImage: (id: string) => setImages((prev) => prev.filter((img) => img.id !== id)),
      clearImages: () => setImages([]),
    }),
    [inputValue, setInputValue, submit, isIdle, images],
  );

  const value = React.useMemo<CartAssistantContextValue>(
    () => ({
      currentThreadId: session.id,
      messages: legacyMessages,
      thread: { thread: { messages: legacyMessages, status } },
      streamingState: { status: isStreaming ? "streaming" : isWaiting ? "waiting" : "idle" },
      isStreaming,
      isWaiting,
      isIdle,
      error,
      sendMessage,
      cancelRun,
      startNewThread,
      switchThread,
      input,
      threads,
      threadsLoading,
      refetchThreads,
      deleteThread,
      renameThread,
      loadThreadMessages,
      suggestions,
      isGeneratingSuggestions,
    }),
    [
      session.id, legacyMessages, status, isStreaming, isWaiting, isIdle, error, sendMessage, cancelRun,
      startNewThread, switchThread, input, threads, threadsLoading, refetchThreads, deleteThread,
      renameThread, loadThreadMessages, suggestions, isGeneratingSuggestions,
    ],
  );

  return <CartAssistantContext.Provider value={value}>{children}</CartAssistantContext.Provider>;
}

export const useCartAssistant = () => {
  const ctx = React.useContext(CartAssistantContext);
  if (!ctx) throw new Error("useCartAssistant must be used within CartAssistantProvider");
  return ctx;
};

export const useCartAssistantInput = () => useCartAssistant().input;
